import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Compass, Award, Layers } from "lucide-react";
import { MarsGlobe } from "@/components/MarsGlobe";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function Landing() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/global").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const fmt = (n) => (n == null ? "—" : n.toLocaleString());

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <MarsGlobe interactive={false} enableZoom={false} positionX={1.6} />
        </div>
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-deepspace via-deepspace/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 z-[1] bg-gradient-to-t from-deepspace to-transparent" />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 w-full">
          <div className="max-w-2xl fade-up">
            <div className="text-overline mb-6">{t.landing.tag}</div>
            <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-6xl lg:text-7xl tracking-tight">
              The Portal<br />Universe
            </h1>
            <p className="mt-7 text-cosmic/80 text-base md:text-lg max-w-lg font-light leading-relaxed">
              {t.landing.tagline}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/explore" data-testid="enter-mars-button" className="btn-portal flex items-center gap-2">
                {t.landing.enter} <ArrowRight size={15} />
              </Link>
              <Link to="/lore" data-testid="read-lore-button" className="btn-ghost">
                {t.landing.readLore}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-overline text-[0.6rem] opacity-60">
          {t.landing.scroll}
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="relative z-10 border-y border-cosmic/10 glass">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
          <Stat label={t.landing.statTotal} value={fmt(stats?.total_sectors || 724000)} />
          <Stat label={t.landing.statRegistered} value={fmt(stats?.claimed_sectors)} />
          <Stat label={t.landing.statExplorers} value={fmt(stats?.total_explorers)} />
          <Stat label={t.landing.statSurface} value="200 km²" />
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 py-28">
        <div className="text-overline mb-4">{t.landing.programmeTag}</div>
        <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight max-w-3xl">
          {t.landing.programmeTitle}
        </h2>
        <p className="mt-6 text-cosmic/70 max-w-2xl font-light leading-relaxed">
          {t.landing.programmeBody}
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-cosmic/10 border border-cosmic/10">
          <Pillar icon={<Globe2 />} title={t.landing.pExploreT} body={t.landing.pExploreB} />
          <Pillar icon={<Compass />} title={t.landing.pDiscoverT} body={t.landing.pDiscoverB} />
          <Pillar icon={<Layers />} title={t.landing.pRegisterT} body={t.landing.pRegisterB} />
          <Pillar icon={<Award />} title={t.landing.pLegacyT} body={t.landing.pLegacyB} />
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 pb-28">
        <div className="glass border border-portal/20 p-10 md:p-16 text-center">
          <div className="text-overline mb-5">{t.landing.ctaTag}</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">{t.landing.ctaTitle}</h2>
          <p className="mt-5 text-cosmic/70 max-w-xl mx-auto font-light">
            {t.landing.ctaBody}
          </p>
          <Link to="/explore" className="btn-portal inline-flex items-center gap-2 mt-9">
            {t.landing.enter} <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-6 py-8 border-r border-cosmic/10 last:border-r-0">
      <div className="font-display text-2xl md:text-4xl text-white">{value}</div>
      <div className="text-overline mt-2">{label}</div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="bg-deepspace/40 p-8 hover:bg-portal/5 transition-colors group">
      <div className="text-portal mb-5 group-hover:scale-110 transition-transform origin-left">{icon}</div>
      <h3 className="font-grotesk text-xl mb-3">{title}</h3>
      <p className="text-cosmic/60 text-sm font-light leading-relaxed">{body}</p>
    </div>
  );
}
